export type LatenessWindow = 7 | 14 | 30;

export const LATENESS_WINDOWS: readonly LatenessWindow[] = [7, 14, 30] as const;

export const LATENESS_WINDOW_LABELS: Record<LatenessWindow, string> = {
  7: 'SETTINGS.ACCESS.LATENESS.WINDOW.WEEK',
  14: 'SETTINGS.ACCESS.LATENESS.WINDOW.TWO_WEEKS',
  30: 'SETTINGS.ACCESS.LATENESS.WINDOW.MONTH',
};

export const LATENESS_WINDOW_TITLE_LABELS: Record<LatenessWindow, string> = {
  7: 'SETTINGS.ACCESS.LATENESS.WINDOW_TITLE.WEEK',
  14: 'SETTINGS.ACCESS.LATENESS.WINDOW_TITLE.TWO_WEEKS',
  30: 'SETTINGS.ACCESS.LATENESS.WINDOW_TITLE.MONTH',
};

export interface LatenessWindowCard {
  Window: LatenessWindow;
  TitleKey: string;
  LabelKey: string;
  EnabledField: 'Window7Enabled' | 'Window14Enabled' | 'Window30Enabled';
  LimitField: 'Window7Limit' | 'Window14Limit' | 'Window30Limit';
  BlocksField: 'Window7BlocksEntry' | 'Window14BlocksEntry' | 'Window30BlocksEntry';
}

export interface LatenessWindowRow {
  Card: LatenessWindowCard;
  IsOn: boolean;
  NoticeLabel: string;
  BlockLabel: string;
}

export type LatenessNotification =
  | 'lateEntry'
  | 'nearLimit'
  | 'limitReached'
  | 'entryBlocked';

export const LATENESS_NOTIFICATIONS: readonly LatenessNotification[] = [
  'lateEntry',
  'nearLimit',
  'limitReached',
  'entryBlocked',
] as const;

export const LATENESS_NOTIFICATION_PREFIX = {
  lateEntry: 'LateEntryNotification',
  nearLimit: 'NearLimitNotification',
  limitReached: 'LimitReachedNotification',
  entryBlocked: 'EntryBlockedNotification',
} as const satisfies Record<LatenessNotification, string>;

export type LatenessNotificationPrefix =
  (typeof LATENESS_NOTIFICATION_PREFIX)[LatenessNotification];

export type LatenessEnabledField = `${LatenessNotificationPrefix}Enabled`;
export type LatenessTitleField = `${LatenessNotificationPrefix}Title`;
export type LatenessBodyField = `${LatenessNotificationPrefix}Body`;

export const LATENESS_MERGE_FIELDS = [
  '<<PersonName>>',
  '<<TenantName>>',
  '<<EventDate>>',
  '<<EventTime>>',
  '<<AccessPoint>>',
  '<<SchoolClass>>',
  '<<DelayMinutes>>',
  '<<LateCount>>',
  '<<LateLimit>>',
  '<<LateWindow>>',
] as const;

export type LatenessMergeField = (typeof LATENESS_MERGE_FIELDS)[number];

export const LATENESS_TITLE_MAX_LENGTH = 20;
export const LATENESS_BODY_MAX_LENGTH = 500;
export const LATENESS_BODY_PREVIEW_MAX_LENGTH = 160;
export const LATENESS_LIMIT_MIN = 1;

export interface LatenessSettings {
  LatenessSettingsId: string;
  Window7Enabled: boolean;
  Window7Limit: number | null;
  Window7BlocksEntry: boolean;
  Window14Enabled: boolean;
  Window14Limit: number | null;
  Window14BlocksEntry: boolean;
  Window30Enabled: boolean;
  Window30Limit: number | null;
  Window30BlocksEntry: boolean;
  AllowManualRelease: boolean;
  RequireManualReleaseReason: boolean;
  LateEntryNotificationEnabled: boolean;
  LateEntryNotificationTitle: string;
  LateEntryNotificationBody: string;
  NearLimitNotificationEnabled: boolean;
  NearLimitNotificationTitle: string;
  NearLimitNotificationBody: string;
  LimitReachedNotificationEnabled: boolean;
  LimitReachedNotificationTitle: string;
  LimitReachedNotificationBody: string;
  EntryBlockedNotificationEnabled: boolean;
  EntryBlockedNotificationTitle: string;
  EntryBlockedNotificationBody: string;
  AddedOnUtc: string | null;
  AddedBy: string | null;
  UpdatedOnUtc: string | null;
  UpdatedBy: string | null;
}

export type LatenessSettingsRequest = Partial<
  Omit<
    LatenessSettings,
    'LatenessSettingsId' | 'AddedOnUtc' | 'AddedBy' | 'UpdatedOnUtc' | 'UpdatedBy'
  >
>;

export interface LatenessSettingsState {
  latenessSettings: LatenessSettings | null;
}

export const initialLatenessSettingsState: LatenessSettingsState = {
  latenessSettings: null,
};
